// Reward-calculation editor-state types. The discriminator values match the backend
// (generated `RewardCalculationType`); the interior model shapes are re-declared here as
// UI view-models. The final request is typed against the generated `CreateActionModuleRequest`
// at the save boundary in ActionModuleEditor.
import { RewardCalculationType } from '@/api/model'

export { RewardCalculationType }

export interface BaseRewardCalculationType {
  type: RewardCalculationType
}

export function isBaseRewardCalculationTypeModel(value: unknown): value is BaseRewardCalculationType {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    Object.values(RewardCalculationType).includes((value as BaseRewardCalculationType).type)
  )
}

export interface ScoreRange {
  scoreFrom: number
  scoreTo: number
  pointsRewarded: number
}

export interface FixedRewardCalculationTypeModel extends BaseRewardCalculationType {
  reward: number
}

export function isFixedRewardCalculationTypeModel(value: unknown): value is FixedRewardCalculationTypeModel {
  return (
    isBaseRewardCalculationTypeModel(value) &&
    value.type === RewardCalculationType.FixedRewardCalculationType &&
    'reward' in value &&
    typeof (value as FixedRewardCalculationTypeModel).reward === 'number'
  )
}

export interface ScoreMultiplyRewardCalculationTypeModel extends BaseRewardCalculationType {
  factor: number
}

export function isScoreMultiplyRewardCalculationTypeModel(
  value: unknown,
): value is ScoreMultiplyRewardCalculationTypeModel {
  return (
    isBaseRewardCalculationTypeModel(value) &&
    value.type === RewardCalculationType.ScoreMultiplyRewardCalculationType &&
    'factor' in value &&
    typeof (value as ScoreMultiplyRewardCalculationTypeModel).factor === 'number'
  )
}

export interface ScoreRangeRewardCalculationTypeModel extends BaseRewardCalculationType {
  scoreRanges: ScoreRange[]
}

export function isScoreRangeRewardCalculationTypeModel(
  value: unknown,
): value is ScoreRangeRewardCalculationTypeModel {
  return (
    isBaseRewardCalculationTypeModel(value) &&
    value.type === RewardCalculationType.ScoreRangeCalculationType &&
    'scoreRanges' in value &&
    Array.isArray((value as ScoreRangeRewardCalculationTypeModel).scoreRanges)
  )
}

// The editor works with the model minus its discriminator; the dispatcher re-stamps `type`.
export type EditorRewardCalculationType = Omit<BaseRewardCalculationType, 'type'>

export interface RewardCalculationTypeEditorProps {
  value: EditorRewardCalculationType | null
  onChanged: (value: BaseRewardCalculationType) => void
}
